import React, { useMemo } from 'react'
import {
  Alert,
  AlertIcon,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Select,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'

type CreditLiabilityType = {
  id: string
  name: string
}

const nonRentCreditCheckboxStyles = {
  '.chakra-checkbox__control': {
    bg: 'white',
    borderColor: 'orange.700',
    borderWidth: '2px'
  },
  '.chakra-checkbox__control[data-checked]': {
    bg: 'orange.600',
    borderColor: 'orange.700'
  }
}

export function CreditLiabilityTypeSelect({
  isDisabled,
  liabilityTypeId,
  liabilityTypes,
  onChange
}: {
  isDisabled?: boolean
  liabilityTypeId: string
  liabilityTypes: CreditLiabilityType[]
  onChange: (liabilityTypeId: string) => void
}) {
  const sortedLiabilityTypes = useMemo(
    () => [...liabilityTypes].sort((left, right) => left.name.localeCompare(right.name)),
    [liabilityTypes]
  )

  return (
    <FormControl isRequired isDisabled={isDisabled}>
      <FormLabel>Liability type to credit</FormLabel>
      <Select value={liabilityTypeId} onChange={(event) => onChange(event.target.value)}>
        {sortedLiabilityTypes.map((liabilityType) => (
          <option key={liabilityType.id} value={liabilityType.id}>
            {liabilityType.name}
          </option>
        ))}
      </Select>
    </FormControl>
  )
}

export function NonRentCreditWarning({
  example,
  isChecked,
  isDisabled,
  onChange
}: {
  example: string
  isChecked: boolean
  isDisabled?: boolean
  onChange: (isChecked: boolean) => void
}) {
  return (
    <Alert status='warning' alignItems='start'>
      <AlertIcon />
      <Stack spacing={3}>
        <Text>
          You are choosing to credit a liability type other than Rent. This is usually only correct
          when the tenant has unpaid charges for that liability type.
        </Text>
        <Text fontSize='sm'>{example}</Text>
        <Checkbox
          isChecked={isChecked}
          onChange={(event) => onChange(event.target.checked)}
          isDisabled={isDisabled}
          sx={nonRentCreditCheckboxStyles}
        >
          I am sure I want to credit a liability type other than Rent.
        </Checkbox>
      </Stack>
    </Alert>
  )
}

export function CreditNoteField({
  isDisabled,
  note,
  onChange
}: {
  isDisabled?: boolean
  note: string
  onChange: (note: string) => void
}) {
  const noteError = note.length > 0 && note.trim().length === 0

  return (
    <FormControl isRequired isInvalid={noteError} isDisabled={isDisabled}>
      <FormLabel>Note</FormLabel>
      <Textarea
        minH='120px'
        value={note}
        onChange={(event) => onChange(event.target.value)}
        placeholder='Enter the reason for giving the credit'
      />
      {noteError ? (
        <FormErrorMessage>Enter a note for this credit.</FormErrorMessage>
      ) : (
        <FormHelperText>Required.</FormHelperText>
      )}
    </FormControl>
  )
}
